/**
 * 图片上传器 - 独立模块
 * 版本: 1.0.0
 * 功能: 上传图片 -> 生成设计 -> 选择图片 -> 保存到购物车
 *
 * 依赖:
 * - guest-account.js (游客账号管理)
 * - 全局弹窗 HTML 结构
 * - message-toast.js (消息提示组件)
 */

// 从全局配置获取 API 基础 URL
const DEFAULT_BASE_URL =
  window.THEME_CONFIG?.apiBaseUrl || "https://alb.deliver3d.net:3000";

class ImageUploader {
  constructor(options = {}) {
    // 配置选项
    this.config = {
      // API 配置
      apiBaseUrl: options.apiBaseUrl || DEFAULT_BASE_URL,
      uploadEndpoint:
        options.uploadEndpoint || "/models/createModelsFromImages",
      pollEndpoint: options.pollEndpoint || "/jobs/queryStatus",
      tokenStorageKey: options.tokenStorageKey || "deliver3d_guest_token",

      // 生成配置
      jobNum: options.jobNum || 2, // 生成图片数量
      sourceType: options.sourceType || "cute_single_img2img_deliver3d_long",
      defaultStyle: options.defaultStyle || "Q-Style",

      // 轮询配置
      pollInterval: options.pollInterval || 2000, // 2秒
      maxPollAttempts: options.maxPollAttempts || 500, // 最多轮询500次

      // 文件限制
      maxFileSizeMB: options.maxFileSizeMB || 50,
      uploadTargetKB: options.uploadTargetKB || 100,
      uploadMaxSide: options.uploadMaxSide || 1280,
      acceptFileTypes: options.acceptFileTypes || [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/webp",
        "image/WEBP",
        "image/heic",
        "image/HEIC",
      ],

      // 分辨率限制
      minDimension: options.minDimension || 200, // 最小宽度或高度
      maxDimension: options.maxDimension || 8096, // 最大宽度或高度

      // 回调函数
      onUploadStart: options.onUploadStart || null,
      onUploadProgress: options.onUploadProgress || null,
      onUploadSuccess: options.onUploadSuccess || null,
      onUploadError: options.onUploadError || null,
      onImageSelect: options.onImageSelect || null,
    };

    // 保存产品数据
    this.product = options.product || null;
    this.productData = options.product || null;
    this.productDataPromise = null;
    this.preparedCharacterType = null;

    // 风格映射
    this.styleMapping = {
      Chibi: "Q-Style",
      Pixar: "Pixar-Style",
      Minecraft: "Minecraft-Style",
      Funko: "Funko-Style",
      Realistic: "Realistic-Style",
      "Line Healing Style": "Line-Healing-Style",
      "3D Oil Painting": "3D-Embossed-Lightbox",
      "3D Oil Relief - Natural Wood":"3D-Embossed-Lightbox",
      "3D Oil Relief - Walnut":"3D-Embossed-Lightbox",
      "Oil Painting":"oil_painting"
    }

    //Size
    this.sizes = {
      '6" Portrait (10x15cm)': 6,
      '8" Portrait (15x20cm)': 8,
      '4" Square (10x10cm)':4
    };

    // 状态
    this.abortController = null;
    this.progressAnimation = null;
    this.socket = null;
  }

  /**
   * 连接 WebSocket (socket.io)
   * 参考服务端 register 事件进行注册
   */
  connectSocket() {
    if (this.socket) {
      if (this.socket.connected) {
      } else if (typeof this.socket.connect === "function") {
        this.socket.connect();
      }
      return this.socket;
    }

    const token = this.getStoredAuthToken();
    if (!token) {
      return null;
    }

    const serverUrl = this.config.apiBaseUrl;
    this.socket = io(serverUrl, {
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 2000,
      reconnectionAttempts: Infinity,
      transports: ["websocket", "polling"],
    });

    this.socket.on("connect", () => {
      // 用 Bearer token 注册身份
      this.socket.emit("register", token);
    });

    return this.socket;
  }

  getSocketToken() {
    return this.getStoredAuthToken();
  }

  async waitForSocketToken(timeoutMs = 12000) {
    const startTime = Date.now();
    let guestInitTriggered = false;

    while (Date.now() - startTime < timeoutMs) {
      const token = this.getSocketToken();
      if (token) return token;

      if (!guestInitTriggered && window.GuestAccount?.init) {
        guestInitTriggered = true;
        try {
          await window.GuestAccount.init();
        } catch (error) {}
        continue;
      }

      await this.sleep(250);
    }

    return null;
  }

  async ensureSocketReady(timeoutMs = 12000) {
    const token = await this.waitForSocketToken(timeoutMs);
    if (!token) {
      const error = new Error("WebSocket initialization timeout");
      error.isInternalError = true;
      throw error;
    }

    const startTime = Date.now();
    const socketOwner = window.globalImageUploader || this;

    while (Date.now() - startTime < timeoutMs) {
      const socket =
        typeof socketOwner.connectSocket === "function"
          ? socketOwner.connectSocket()
          : this.connectSocket();

      if (socket?.connected) {
        return socket;
      }

      await this.sleep(250);
    }

    const error = new Error("WebSocket initialization timeout");
    error.isInternalError = true;
    throw error;
  }

  /**
   * 断开 WebSocket
   */
  disconnectSocket() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Initialize uploader
   * @param {string|HTMLElement} triggerSelector - Upload button selector or element
   * @param {string|HTMLElement} inputSelector - File input selector or element
   * @param {string|HTMLElement} hiddenInputSelector - Hidden input selector or element (saves image URL)
   */
  init(triggerSelector, inputSelector, hiddenInputSelector) {
    const trigger =
      typeof triggerSelector === "string"
        ? document.querySelector(triggerSelector)
        : triggerSelector;
    const fileInput =
      typeof inputSelector === "string"
        ? document.querySelector(inputSelector)
        : inputSelector;
    const hiddenInput =
      typeof hiddenInputSelector === "string"
        ? document.querySelector(hiddenInputSelector)
        : hiddenInputSelector;

    if (!trigger || !fileInput || !hiddenInput) {
      return;
    }

    this.trigger = trigger;
    this.fileInput = fileInput;
    this.hiddenInput = hiddenInput;

    // 保存事件处理器引用（用于后续清理）
    this.triggerHandler = () => {
      // 清除错误状态（红色边框）
      if (trigger) {
        trigger.classList.remove("validation-error");
      }

      // 清除状态文本（从触发器的ID推断状态元素ID）
      const triggerId = trigger.id;
      let statusId = "ai-upload-status";
      if (triggerId === "ai-upload-trigger-2") {
        statusId = "ai-upload-status-2";
      }
      const statusDiv = document.getElementById(statusId);
      if (statusDiv) {
        statusDiv.textContent = "";
        statusDiv.className = "ai-upload-status";
      }

      // Track upload click event
      if (typeof GtagUtils !== "undefined") {
        GtagUtils.trackUploadClick();
      }

      fileInput.click();
    };
    this.fileInputHandler = (e) => {
      this.handleFileSelect(e);
    };

    // 绑定事件
    trigger.addEventListener("click", this.triggerHandler);
    fileInput.addEventListener("change", this.fileInputHandler);
  }

  async detectHeifBrand(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const arr = new Uint8Array(e.target.result);
        // ISO BMFF brand at offset 4. HEIC is allowed; generic HEIF is blocked.
        if (arr.length < 12) {
          resolve({ isHeic: false, isHeif: false });
          return;
        }
        const header = String.fromCharCode(...arr.slice(4, 12));
        const hasFtyp = header.includes("ftyp");
        resolve({
          isHeic:
            hasFtyp &&
            (header.includes("heic") ||
              header.includes("heix") ||
              header.includes("hevc") ||
              header.includes("hevx")),
          isHeif:
            hasFtyp &&
            (header.includes("heif") ||
              header.includes("heifs") ||
              header.includes("mif1") ||
              header.includes("msf1")),
        });
      };
      reader.onerror = () => resolve({ isHeic: false, isHeif: false });
      reader.readAsArrayBuffer(file.slice(0, 12));
    });
  }

  /**
   * 处理文件选择
   */
  async handleFileSelect(event) {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    // 验证文件
    const validation = this.validateFile(file);
    if (!validation.valid) {
      if (typeof MessageToast !== "undefined") {
        MessageToast.error(validation.message);
      } else {
        alert(validation.message);
      }
      event.target.value = "";
      return;
    }

    const heifBrand = await this.detectHeifBrand(file);
    if (heifBrand.isHeif) {
      if (typeof MessageToast !== "undefined") {
        MessageToast.error(
          "HEIF images are not supported. Please upload HEIC, JPG, PNG, or WEBP.",
        );
      } else {
        alert(
          "HEIF images are not supported. Please upload HEIC, JPG, PNG, or WEBP.",
        );
      }
      event.target.value = "";
      return;
    }

    // 等待全局弹窗初始化
    if (!window.globalUploadModal) {
      setTimeout(() => event.target.dispatchEvent(new Event("change")), 100);
      return;
    }

    // 预处理图片格式：最终上传统一压成 WEBP
    let convertedFile = file; // 使用新变量存储转换后的文件
    const fileName = file.name.toLowerCase();
    const isHeic =
      fileName.endsWith(".heic") ||
      file.type === "image/heic" ||
      heifBrand.isHeic;

    if (isHeic) {
      try {
        convertedFile = await this.convertHeicToJpg(file);
      } catch (error) {
        if (typeof MessageToast !== "undefined") {
          MessageToast.error(error.message);
        } else {
          alert(error.message);
        }
        event.target.value = "";
        return;
      }
    }

    // 预处理 WEBP 格式：转换为 JPG，避免小文件跳过压缩时仍为 webp
    const isWebp =
      fileName.endsWith(".webp") || convertedFile.type === "image/webp";
    if (isWebp) {
      try {
        convertedFile = await this.convertWebpToJpg(convertedFile);
      } catch (error) {
        // 转换失败继续使用原文件，后续压缩步骤仍会尝试转换
      }
    }

    // 获取图片尺寸并检查是否需要转换为正方形
    try {
      const { width, height } = await this.getImageDimensions(convertedFile);

      // 验证图片分辨率
      const minDim = this.config.minDimension;
      const maxDim = this.config.maxDimension;

      if (width < minDim || height < minDim) {
        if (typeof MessageToast !== "undefined") {
          MessageToast.error(
            `Image resolution too small. Minimum ${minDim}x${minDim} pixels required. Your image: ${width}x${height}`,
          );
        } else {
          alert(
            `Image resolution too small. Minimum ${minDim}x${minDim} pixels required. Your image: ${width}x${height}`,
          );
        }
        event.target.value = "";
        return;
      }

      if (width > maxDim || height > maxDim) {
        if (typeof MessageToast !== "undefined") {
          MessageToast.error(
            `Image resolution too large. Maximum ${maxDim}x${maxDim} pixels allowed. Your image: ${width}x${height}`,
          );
        } else {
          alert(
            `Image resolution too large. Maximum ${maxDim}x${maxDim} pixels allowed. Your image: ${width}x${height}`,
          );
        }
        event.target.value = "";
        return;
      }

    } catch (error) {
      // 即使获取尺寸失败，也继续上传原图
    }

    // Open the modal immediately. Product context loads in parallel and is
    // awaited only when the user starts generation.
    this.preparedCharacterType = null;
    const productContextPromise = this.prepareProductContext(convertedFile);

    // 显示弹窗（此时 convertedFile 已完成预处理，上传前还会进一步压成 WEBP）
    // sourceType is passed from the Quick Generate / Better Generate buttons
    // 第二个参数是确认上传回调，第三个参数是重新生成回调
    window.globalUploadModal.show(
      convertedFile,
      "person",

      async (confirmedFile, sourceType) => {
        // If file is null, this is a history selection - just get the URL and save it
        if (!confirmedFile) {
          const selectedUrl = window.globalUploadModal.getSelectedImageUrl();
          const selectedDisplayUrl =
            window.globalUploadModal.getSelectedDisplayUrl();
          if (selectedUrl) {
            this.saveImageUrl(selectedUrl, selectedDisplayUrl);
            if (this.config.onImageSelect) {
              this.config.onImageSelect(selectedDisplayUrl || selectedUrl);
            }
            // Update preview image - prefer display URL for visual
            if (this.trigger) {
              const previewImg =
                this.trigger.querySelector(".ai-upload-preview");
              if (previewImg) {
                previewImg.src = selectedDisplayUrl || selectedUrl;
                this.trigger.classList.add("has-image");
              }
            }
          }
          return;
        }

        // Update config sourceType if provided (from generate buttons)
        if (sourceType) {
          this.config.sourceType = sourceType;
        }
        await productContextPromise;
        await this.processUpload(confirmedFile);
      },
      async (originalFile) => {
        // 重新生成时跳过进度条初始化（regenerate() 已经处理了）
        await productContextPromise;
        await this.processUpload(originalFile, true);
      },
    );

    event.target.value = "";
  }

  getCurrentProductHandle() {
    if (this.product?.handle) return this.product.handle;

    return window.location.pathname
      .split("/products/")[1]
      ?.split("?")[0] || "";
  }

  async loadProductData() {
    if (this.productData) return this.productData;
    if (this.productDataPromise) return this.productDataPromise;

    const productHandle = this.getCurrentProductHandle();
    if (!productHandle) return null;

    window.__imageUploaderProductDataCache ||= new Map();
    const cache = window.__imageUploaderProductDataCache;

    if (!cache.has(productHandle)) {
      cache.set(
        productHandle,
        fetch(`/products/${productHandle}.js`)
          .then((response) => {
            if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
            return response.json();
          })
          .catch((error) => {
            cache.delete(productHandle);
            throw error;
          }),
      );
    }

    this.productDataPromise = cache.get(productHandle);
    try {
      this.productData = await this.productDataPromise;
      return this.productData;
    } finally {
      this.productDataPromise = null;
    }
  }

  async prepareProductContext(file) {
    try {
      await this.fetchSelectedOptions();
      this.preparedCharacterType = await this.detectCharacterType();

      if (window.globalUploadModal?.currentFile === file) {
        window.globalUploadModal.characterType = this.preparedCharacterType;
      }
      return this.preparedCharacterType;
    } catch (error) {
      this.preparedCharacterType = "person";
      return this.preparedCharacterType;
    }
  }

  /**
   * 获取当前选中的产品选项（尺寸和风格）
   */
  async fetchSelectedOptions() {
    const container = this.getCurrentContainer();
    const productForm = container.querySelector('form[id*="product-form"]');
    const variantIdInput = productForm?.querySelector('input[name="id"]');

    if (!variantIdInput) {
      return;
    }

    const variantId = variantIdInput.value;
    const productHandle = this.getCurrentProductHandle();

    if (!productHandle) {
      return;
    }

    try {
      const productData = await this.loadProductData();
      if (!productData) return;
      const currentVariant = productData.variants.find(
        (v) => v.id == variantId,
      );

      if (currentVariant) {
        const selectedSize = currentVariant.option1;
        const selectedStyle = currentVariant.option2;

        // 保存到全局变量
        window.selectedProductOptions = {
          size: selectedSize,
          style: selectedStyle,
          variantId: variantId,
          productHandle: productHandle,
        };
      }
    } catch (err) {}
  }

  /**
   * 验证文件
   */
  validateFile(file) {
    // 检查文件类型 - 通过 MIME 类型或文件扩展名
    const fileName = file.name.toLowerCase();
    if (this.isUnsupportedHeifFile(file)) {
      return {
        valid: false,
        message:
          "HEIF images are not supported. Please upload HEIC, JPG, PNG, or WEBP.",
      };
    }

    const isValidType =
      this.config.acceptFileTypes.includes(file.type) ||
      fileName.endsWith(".jpg") ||
      fileName.endsWith(".jpeg") ||
      fileName.endsWith(".png") ||
      fileName.endsWith(".webp") ||
      fileName.endsWith(".heic");

    if (!isValidType) {
      return {
        valid: false,
        message: "Please select an image file (JPEG, JPG, PNG, WEBP, HEIC)",
      };
    }

    // 检查文件大小
    const fileSizeMB = file.size / 1024 / 1024;
    if (fileSizeMB > this.config.maxFileSizeMB) {
      return {
        valid: false,
        message: `File size exceeds ${this.config.maxFileSizeMB}MB limit`,
      };
    }

    return { valid: true };
  }

  isUnsupportedHeifFile(file) {
    const fileName = file.name.toLowerCase();
    const fileType = (file.type || "").toLowerCase();

    return (
      fileName.endsWith(".heif") ||
      fileName.endsWith(".heifs") ||
      fileType === "image/heif" ||
      fileType === "image/heif-sequence"
    );
  }

  /**
   * 将 HEIC 图片转换为 JPG
   */
  async convertHeicToJpg(file) {
    try {
      // 使用 heic-to 库转换 HEIC 到 JPEG
      if (typeof HeicTo === "undefined") {
        await this.loadHeicTo();
      }

      // 检查是否是 HEIC 文件
      const isHeic = await HeicTo.isHeic(file);
      if (!isHeic) {
        return file;
      }

      // 转换为 JPEG
      const jpegBlob = await HeicTo({
        blob: file,
        type: "image/jpeg",
        quality: 0.95,
      });

      const jpgFile = new File(
        [jpegBlob],
        file.name.replace(/\.heic$/i, ".jpg").replace(/\.heif$/i, ".jpg"),
        { type: "image/jpeg" },
      );

      return jpgFile;
    } catch (error) {
      throw new Error(
        "Failed to convert HEIC image. Please use JPG or PNG format.",
      );
    }
  }

  /**
   * 动态加载 heic-to 库
   */
  async loadHeicTo() {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/heic-to@1.3.0/dist/iife/heic-to.js";
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        reject(new Error("Failed to load heic-to library"));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * 将 WEBP 图片转换为 JPG
   */
  async convertWebpToJpg(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const jpgFile = new File(
                  [blob],
                  file.name.replace(/\.webp$/i, ".jpg"),
                  { type: "image/jpeg" },
                );
                resolve(jpgFile);
              } else {
                reject(new Error("Failed to convert image to JPG"));
              }
            },
            "image/jpeg",
            0.95,
          );
        };

        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = e.target.result;
      };

      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  }

  /**
   * 获取图片尺寸
   * @param {File} file - 图片文件
   * @returns {Promise<{width: number, height: number}>}
   */
  async getImageDimensions(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      // Use createObjectURL for better reliability
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        const result = { width: img.width, height: img.height };
        URL.revokeObjectURL(objectUrl); // Clean up
        resolve(result);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl); // Clean up
        reject(new Error("Failed to load image"));
      };

      img.src = objectUrl;
    });
  }

  /**
   * 将长图转换为正方形（添加白色背景填充）
   * @param {File} file - 图片文件
   * @param {number} width - 图片宽度
   * @param {number} height - 图片高度
   * @returns {Promise<File>}
   */
  async convertToSquare(file, width, height) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          // 计算正方形边长（取长边）
          const size = Math.max(width, height);

          // 创建正方形 canvas
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;

          const ctx = canvas.getContext("2d");
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";

          // 填充白色背景
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, size, size);

          // 计算图片居中位置
          const x = (size - width) / 2;
          const y = (size - height) / 2;

          // 绘制图片（居中）
          ctx.drawImage(img, x, y, width, height);

          // 转换为 Blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const squareFile = new File(
                  [blob],
                  file.name.replace(/\.\w+$/, "_square.jpg"),
                  { type: "image/jpeg" },
                );
                resolve(squareFile);
              } else {
                reject(new Error("Failed to convert to square"));
              }
            },
            "image/jpeg",
            0.9,
          );
        };

        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = e.target.result;
      };

      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  }

  /**
   * 压缩图片（带目标大小限制）
   * @param {File} file - 原始图片文件
   * @param {number} targetKB - 目标文件大小（KB），如果不指定则使用默认压缩
   * @returns {Promise<File>} 压缩后的图片文件
   */
  async compressImageWithLimit(file, targetKB = null) {
    return new Promise((resolve) => {
      if (targetKB && file.size <= targetKB * 1024) {
        resolve(file);
        return;
      }

      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = async () => {
        const { width, height } = img;

        // 创建 canvas
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // 如果图片尺寸过大，先缩小尺寸
        let currentWidth = width;
        let currentHeight = height;
        const maxSide = Math.max(width, height);
        if (maxSide > this.config.uploadMaxSide) {
          const scale = this.config.uploadMaxSide / maxSide;
          currentWidth = Math.max(1, Math.round(width * scale));
          currentHeight = Math.max(1, Math.round(height * scale));
        }

        const drawToCanvas = () => {
          canvas.width = currentWidth;
          canvas.height = currentHeight;
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.clearRect(0, 0, currentWidth, currentHeight);
          ctx.drawImage(img, 0, 0, currentWidth, currentHeight);
        };

        const createJpegBlob = (quality) =>
          new Promise((resolveBlob) => {
            canvas.toBlob(resolveBlob, "image/jpeg", quality);
          });

        drawToCanvas();

        // 如果没有指定目标大小，使用默认质量压缩
        if (!targetKB) {
          const blob = await createJpegBlob(0.82);
          URL.revokeObjectURL(objectUrl);
          resolve(
            new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
              type: "image/jpeg",
            }),
          );
          return;
        }

        // 二分查找最佳压缩质量，使文件大小接近目标值
        const targetBytes = targetKB * 1024;
        let blob = null;
        const minSide = 320;

        // 最多迭代10次寻找最佳质量
        for (let attempt = 0; attempt < 6; attempt++) {
          let minQ = 0.05;
          let maxQ = 0.95;
          let bestBlob = null;
          let smallestBlob = null;

          for (let i = 0; i < 12; i++) {
            const quality = (minQ + maxQ) / 2;
            const candidate = await createJpegBlob(quality);
            if (!candidate) continue;

            if (!smallestBlob || candidate.size < smallestBlob.size) {
              smallestBlob = candidate;
            }

            if (candidate.size <= targetBytes) {
              bestBlob = candidate;
              minQ = quality;
            } else {
              maxQ = quality;
            }
          }

          blob = bestBlob || smallestBlob;
          if (!blob) break;
          if (blob.size <= targetBytes) break;
          if (Math.min(currentWidth, currentHeight) <= minSide) break;

          currentWidth = Math.max(minSide, Math.round(currentWidth * 0.85));
          currentHeight = Math.max(minSide, Math.round(currentHeight * 0.85));
          drawToCanvas();
        }

        if (!blob) {
          URL.revokeObjectURL(objectUrl);
          resolve(file);
          return;
        }

        const compressedFile = new File(
          [blob],
          file.name.replace(/\.\w+$/, ".jpg"),
          {
            type: "image/jpeg",
          },
        );

        URL.revokeObjectURL(objectUrl);
        resolve(compressedFile);
      };

      img.onerror = () => {
        resolve(file); // 如果加载失败，返回原文件
      };

      img.src = objectUrl;
    });
  }

  /**
   * 处理上传流程
   * @param {File} file - 要上传的文件
   * @param {boolean} [skipProgressInit=false] - 是否跳过进度条初始化（用于重新生成时）
   */
  async processUpload(file, skipProgressInit = false) {
    try {
      // Track upload count for order_placed analytics
      this.safeInvoke("increment upload tracking", () => {
        if (typeof GtagTrackingHelpers !== "undefined") {
          GtagTrackingHelpers.incrementUploadCount();
        }
      });

      // 检查并锁定抽屉，防止在上传过程中被关闭
      const drawer = document.querySelector("quick-buy-drawer[open]");
      if (drawer) {
        drawer.dataset.uploadInProgress = "true";
      }

      // 注意：基础格式处理已经在 handleFileChange() 中完成
      // Quick 生成会在上传前补白边，Better 则保持原图

      if (this.config.sourceType === "cute_single_img2img_deliver3d_sd") {
        try {
          const { width, height } = await this.getImageDimensions(file);
          file = await this.convertToSquare(file, width, height);
        } catch (error) {}
      }

      // 上传前压缩成更小的 WEBP，减少 createModelsFromImages 接口耗时
      file = await this.compressImageWithLimit(
        file,
        this.config.uploadTargetKB,
      );

      // 回调: 上传开始
      this.safeInvoke("onUploadStart callback", () => this.config.onUploadStart?.(file));

      // 立即设置图片选择回调（在轮询开始前）
      // 这样用户选择图片后点击确认就会直接关闭，不会重新触发上传
      window.globalUploadModal.confirmCallback = () => {
        const selectedUrl = window.globalUploadModal.getSelectedImageUrl();
        const selectedDisplayUrl =
          window.globalUploadModal.getSelectedDisplayUrl();
        const selectedJobId = window.globalUploadModal.getSelectedJobId();

        if (selectedUrl) {
          // 停止轮询（如果还在进行中）
          if (window.globalUploadModal.abortController) {
            window.globalUploadModal.abortController.abort();
          }

          // 停止进度动画
          if (this.progressAnimation) {
            this.progressAnimation.stop();
          }

          this.saveImageUrl(selectedUrl, selectedDisplayUrl, selectedJobId);

          // 回调: 图片选择 (pass displayUrl for preview)
          if (this.config.onImageSelect) {
            this.config.onImageSelect(selectedDisplayUrl || selectedUrl);
          }

          // 解锁抽屉
          const drawer = document.querySelector("quick-buy-drawer");
          if (drawer) drawer.dataset.uploadInProgress = "false";

          window.globalUploadModal.close();
        }
      };

      // 显示进度条（除非是重新生成，进度条已由 regenerate() 初始化）
      if (!skipProgressInit) {
        window.globalUploadModal.showProgress();

        // 根据 sourceType 设置进度条时长
        // Quick Generate: 27秒, Better Generate: 55秒
        const progressDuration =
          this.config.sourceType === "cute_single_img2img_deliver3d_sd"
            ? 35000
            : 47000;
        this.progressAnimation =
          window.globalUploadModal.startProgressAnimation(progressDuration);
      }

      // 上传文件
      const uploadResult = await this.uploadImage(file);

      // 轮询获取生成结果（传入 uuids 用于精确查询）
      const generatedImages = await this.pollImages(uploadResult);

      // 停止进度动画
      if (this.progressAnimation) {
        this.progressAnimation.stop();
      }

      // 回调: 上传成功
      this.safeInvoke("onUploadSuccess callback", () =>
        this.config.onUploadSuccess?.(generatedImages),
      );
    } catch (error) {
      const userFacingError = this.getUserFacingError(error);
      // 用户取消
      if (error.name === "AbortError") {
        // 解锁抽屉
        const drawer = document.querySelector("quick-buy-drawer[open]");
        if (drawer) drawer.dataset.uploadInProgress = "false";
        return;
      }

      // 停止进度动画
      if (this.progressAnimation) {
        this.progressAnimation.stop();
      }

      // 显示友好的错误提示
      if (error.isAllFailed) {
        // 全部失败错误：已经在 pollImages 中显示过了，不需要再次显示
      } else if (error.isRateLimit) {
        // 限频错误：显示特殊提示
        window.globalUploadModal.showError("General error", userFacingError.message);
      } else if (this.isNetworkError(error)) {
        // 网络错误：显示网络提示
        this.trackUploadNetworkError(error, userFacingError);
        window.globalUploadModal.showError(
          "Network Error",
          userFacingError.message,
          null,
        );
      } else {
        // 其他错误：显示通用错误提示
        this.trackSystemBusyError(error, userFacingError);
        window.globalUploadModal.showError(
          "System Busy",
          userFacingError.message,
          null,
        );
      }

      // 解锁抽屉
      const drawer = document.querySelector("quick-buy-drawer");
      if (drawer) drawer.dataset.uploadInProgress = "false";

      // 回调: 上传错误
      this.safeInvoke("onUploadError callback", () =>
        this.config.onUploadError?.(userFacingError),
      );

      // 不关闭弹窗，让用户看到错误信息
      // window.globalUploadModal.close();
    }
  }

  safeInvoke(label, fn) {
    if (typeof fn !== "function") return undefined;
    try {
      return fn();
    } catch (error) {
      return undefined;
    }
  }

  trackSystemBusyError(error, userFacingError) {
    try {
      if (typeof GtagUtils === "undefined" || typeof GtagUtils.sendEvent !== "function") {
        return;
      }

      const modelName =
        typeof GtagUtils.getModelName === "function"
          ? GtagUtils.getModelName(this.config.sourceType)
          : "";

      if (!GtagUtils.shouldTrack("system_busy_error_" + this.getSystemBusyErrorReason(error), 1000)) return;

      GtagUtils.sendEvent("system_busy_error", {
        error_type: "system_busy",
        error_code: this.getSystemBusyErrorReason(error),
        flow_id: GtagUtils.getFlowId(),
        generation_model: modelName,
        endpoint: this.config.uploadEndpoint || "",
        has_token: this.hasAuthToken() ? "yes" : "no",
      });
    } catch (trackingError) {}
  }

  trackUploadNetworkError(error, userFacingError) {
    try {
      if (typeof GtagUtils === "undefined" || typeof GtagUtils.sendEvent !== "function") {
        return;
      }

      const modelName =
        typeof GtagUtils.getModelName === "function"
          ? GtagUtils.getModelName(this.config.sourceType)
          : "";

      const failureStage = this.getNetworkErrorStage(error);
      if (!GtagUtils.shouldTrack("upload_network_error_" + failureStage, 1000)) return;

      GtagUtils.sendEvent("upload_network_error", {
        error_type: "network",
        error_code: String(error?.name || "network_error").toLowerCase(),
        failure_stage: failureStage,
        flow_id: GtagUtils.getFlowId(),
        generation_model: modelName,
        endpoint: this.config.uploadEndpoint || "",
        has_token: this.hasAuthToken() ? "yes" : "no",
      });
    } catch (trackingError) {}
  }

  getNetworkErrorStage(error) {
    const explicitStage = error?.sourceStage || error?.source_stage || error?.originalError?.sourceStage;
    if (explicitStage) return explicitStage;

    const message = String(error?.message || "").toLowerCase();
    const tokenInitMessage = String(window.GuestAccount?.lastInitError?.message || "").toLowerCase();
    const combinedMessage = [message, tokenInitMessage].join(" ");

    if (combinedMessage.includes("/auth/tempuserautologin")) return "guest_temp_user_auto_login";
    if (combinedMessage.includes("websocket initialization timeout")) return "websocket_init";
    if (combinedMessage.includes("processing timeout")) return "generation_poll_timeout";
    if (combinedMessage.includes(this.config.uploadEndpoint.toLowerCase())) return "upload";
    if (combinedMessage.includes(this.config.pollEndpoint.toLowerCase())) return "poll";

    return "unknown";
  }

  markErrorSourceStage(error, sourceStage) {
    if (error && typeof error === "object" && !error.sourceStage) {
      error.sourceStage = sourceStage;
    }
    return error;
  }

  getSystemBusyErrorReason(error) {
    if (!error) return "missing_error";

    const message = String(error.message || "").toLowerCase();
    const name = String(error.name || "").toLowerCase();
    const originalMessage = String(error.originalError?.message || "").toLowerCase();
    const tokenInitMessage = String(window.GuestAccount?.lastInitError?.message || "").toLowerCase();
    const combinedMessage = [message, originalMessage, tokenInitMessage].join(" ");

    if (combinedMessage.includes("no authentication token")) return "missing_auth_token";
    if (combinedMessage.includes("no token received from temp user auto login")) {
      return "guest_temp_user_auto_login_missing_token";
    }
    if (combinedMessage.includes("temp user auto login failed")) {
      return "guest_temp_user_auto_login_failed";
    }
    if (combinedMessage.includes("no token received from login")) return "guest_login_missing_token";
    if (combinedMessage.includes("get tourist token failed")) return "guest_temp_user_info_failed";
    if (combinedMessage.includes("register failed")) return "guest_register_failed";
    if (combinedMessage.includes("login failed")) return "guest_login_failed";
    if (name === "syntaxerror") return "invalid_json_response";
    if (combinedMessage.includes("invalid response from server")) return "invalid_upload_response";
    if (combinedMessage.includes("upload failed")) return "upload_failed";
    if (combinedMessage.includes("server error")) return "poll_server_error";
    if (combinedMessage.includes("websocket initialization timeout")) return "websocket_initialization_timeout";
    if (
      combinedMessage.includes("failed to convert") ||
      combinedMessage.includes("failed to read file") ||
      combinedMessage.includes("failed to load image") ||
      combinedMessage.includes("failed to load heic-to library")
    ) {
      return "image_processing_error";
    }

    return "unclassified_internal_error";
  }

  hasAuthToken() {
    return Boolean(this.getStoredAuthToken());
  }

  /**
   * 获取当前活跃的产品容器（抽屉/popover/主页面）
   * @returns {HTMLElement} 当前活跃的容器元素
   */
  getCurrentContainer() {
    // Prefer the container that owns this uploader. A closed quick-buy element
    // can retain its `open` attribute while its transition finishes, so a
    // document-level lookup may otherwise leak its selected variant into the
    // main product uploader.
    if (this.trigger) {
      const quickBuyContainer = this.trigger.closest(
        "quick-buy-drawer, quick-buy-popover",
      );
      if (quickBuyContainer) {
        return quickBuyContainer;
      }

      return this.trigger.closest(".product-form, .quick-buy-product") ||
        this.trigger.closest(".main-product") ||
        document.querySelector(".main-product") ||
        document.body;
    }

    // Compatibility fallback for callers that have not initialized a trigger.
    const openDrawer = document.querySelector("quick-buy-drawer[open]");
    if (openDrawer) {
      return openDrawer;
    }

    const openPopover = document.querySelector("quick-buy-popover[open]");
    if (openPopover) {
      return openPopover;
    }

    return document.querySelector(".main-product") || document.body;
  }

  /**
   * 检测角色类型（pet、person、couple、human-pet 或 keychain）
   * @returns {string} 'pet'、'person'、'couple'、'human-pet' 或 'keychain'
   */
  async detectCharacterType() {
    let characterType = "person"; // 默认值

    // Reset fridge magnet flag
    this._isFridgeMagnet = false;

    // 获取当前活跃的容器
    const container = this.getCurrentContainer();

    // 0. 获取商品名称
    let titleText = "";
    let productHandle = this.getCurrentProductHandle();

    // 方法1: 从表单中获取产品 handle，然后异步获取产品信息
    const productForm = container.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      if (productHandle) {
        // 尝试从 Shopify 的产品 JSON 获取标题
        try {
          // 检查是否有产品数据脚本标签
          const productDataScript = container.querySelector(
            'script[type="application/json"][data-product-json]',
          );
          if (productDataScript) {
            const productData = JSON.parse(productDataScript.textContent);
            titleText = productData.title;
          } else {
            const productData = await this.loadProductData();
            titleText = productData?.title || "";
          }
        } catch (e) {}
      }
    }

    // 方法2: 从容器中查找标题元素（降级方案）
    if (!titleText) {
      const titleSelectors = [
        ".product-item-meta__title",
        ".product__title",
        ".product-meta__title",
        "h1",
        '[class*="title"]',
      ];

      for (const selector of titleSelectors) {
        const productTitle = container.querySelector(selector);
        if (productTitle && productTitle.textContent.trim()) {
          titleText = productTitle.textContent.trim();
          break;
        }
      }
    }

    // 方法3: 如果在容器中找不到标题，尝试使用 Shopify 全局对象（仅产品详情页）
    if (
      !titleText &&
      typeof window.ShopifyAnalytics !== "undefined" &&
      window.ShopifyAnalytics.meta &&
      window.ShopifyAnalytics.meta.product
    ) {
      titleText = window.ShopifyAnalytics.meta.product.name || "";
    }

    // 检查标题是否包含 "1 person + 1 pet"
    const hasHumanPetInTitle = /1\s*person\s*\+\s*1\s*pet/i.test(titleText);
    let hasPetInTitle = /\bpet\b/i.test(titleText);

    // 1. 检查当前选择的变体选项（在当前容器内查找）
    const allSelectedOptions = container.querySelectorAll(
      'input[type="radio"][name^="option"]:checked',
    );
    let hasPetInOption = false;
    let hasCoupleInOption = false;
    let hasHumanPetInOption = false;
    let hasPersonInOption = false; // 新增：检查是否有人物选项
    let hasSingleInOption = false; // 检查是否有 "Single" 选项
    let hasMultipleInOption = false; // 检查是否有 "Multiple" 选项

    allSelectedOptions.forEach((radio) => {
      const optionValue = radio.value || "";
      const optionText = radio.getAttribute("data-option-value") || optionValue;

      // 检查是否包含 "Fridge magnet" - 如果是，强制返回 person
      if (/fridge\s*magnet/i.test(optionText)) {
        characterType = "person";
        // Set flag to indicate this is a fridge magnet product (treated like isBadge)
        this._isFridgeMagnet = true;
      }

      // 检查是否包含 "Single" - 返回 person
      if (/\bsingle\b/i.test(optionText)) {
        hasSingleInOption = true;
      }

      // 检查是否包含 "Multiple" - 返回 group
      if (/\bmultiple\b/i.test(optionText)) {
        hasMultipleInOption = true;
      }

      // 检查是否包含 "1 person + 1 pet"
      if (/1\s*person\s*\+\s*1\s*pet/i.test(optionText)) {
        hasHumanPetInOption = true;
      }

      // 检查是否包含 "1 person"、"2 people" 等人物选项
      if (
        /\d+\s*person/i.test(optionText) ||
        /\d+\s*people/i.test(optionText)
      ) {
        hasPersonInOption = true;
      }

      // 检查是否包含 "pet" 或 "Pet"
      if (/\bpet\b/i.test(optionText)) {
        hasPetInOption = true;
      }

      // 检查是否包含 "2 people (connected)" 或 "couple" 或 "Couple"
      if (
        /2\s*people\s*\(\s*connected\s*\)/i.test(optionText) ||
        /2\s*people\s*\(connected\)/i.test(optionText) ||
        /2\s*people\s*\(\s*together\s*\)/i.test(optionText) ||
        /\bcouple\b/i.test(optionText)
      ) {
        hasCoupleInOption = true;
      }
    });

    // 如果是 Fridge magnet 产品，直接返回 person（优先级最高）
    if (this._isFridgeMagnet) {
      return "person";
    }

    // 如果选项包含 "Multiple"，返回 group
    if (hasMultipleInOption) {
      return "group";
    }

    // 如果选项中有人物相关（1 person、2 people 等），强制 hasPetInTitle 为 false
    if (hasPersonInOption) {
      hasPetInTitle = false;
    }

    // 如果选项中有 pet，但也有 person/people，优先使用选项中的 pet 判断
    hasPetInOption = hasPetInOption || hasPetInTitle;

    // 2. 如果标题或选项包含 "1 person + 1 pet"，返回 human-pet
    if (hasHumanPetInTitle || hasHumanPetInOption) {
      return "human-pet";
    }

    // 3. 如果标题或选项包含 pet，返回 pet
    if (hasPetInOption) {
      return "pet";
    }

    // 4. 如果选项包含 "2 people (connected)" 或 "couple"，返回 couple
    if (hasCoupleInOption) {
      return "couple";
    }

    // 5. 如果选项包含 "Single"，返回 person
    if (hasSingleInOption) {
      return "person";
    }

    // 6. 检查商品标签是否包含 "Personalized Figure Keychains"
    // const productForm = container.querySelector('form[is="product-form"]');
    // const productTagsString = productForm?.getAttribute('data-product-tags') || '';

    // if (productTagsString.includes('personalized figure keychains')) {
    //   return 'keychain';
    // }

    // 6. 默认返回 person
    return characterType;
  }

  /**
   * Detect if current product is a Badge product (or Fridge magnet)
   * @returns {boolean} Whether this is a Badge product or Fridge magnet
   */
  detectIsBadge() {
    // Check if Fridge magnet was detected (set by detectCharacterType)
    if (this._isFridgeMagnet) {
      return true;
    }

    // Get current active container
    const container = this.getCurrentContainer();

    // Check for "Fridge magnet" option directly (in case detectCharacterType wasn't called first)
    const allSelectedOptions = container.querySelectorAll(
      'input[type="radio"][name^="option"]:checked',
    );
    for (const radio of allSelectedOptions) {
      const optionValue = radio.value || "";
      const optionText = radio.getAttribute("data-option-value") || optionValue;
      if (/fridge\s*magnet/i.test(optionText)) {
        this._isFridgeMagnet = true;
        return true;
      }
    }

    // Read isBadge from product metafields JSON
    const metafieldsScript = container.querySelector(
      ".variant-metafields-data",
    );
    if (metafieldsScript) {
      try {
        const data = JSON.parse(metafieldsScript.textContent);
        const productMetafields = data.productMetafields || {};

        const isBadge = productMetafields.isBadge;
        if (
          isBadge === "yes" ||
          isBadge === "YES" ||
          isBadge === "Yes" ||
          isBadge === true
        ) {
          return true;
        }
      } catch (e) {}
    }

    // Fallback: Get from global variable
    const selectedOptions = window.selectedProductOptions || {};
    if (selectedOptions.isBadge === "yes" || selectedOptions.isBadge === true) {
      return true;
    }

    return false;
  }

  /**
   * Detect if current product is a Keychain product
   * @returns {boolean} Whether this is a Keychain product
   */
  detectIsKeychain() {
    // Get current active container
    const container = this.getCurrentContainer();

    // Read isKeychain from product metafields JSON
    const metafieldsScript = container.querySelector(
      ".variant-metafields-data",
    );
    if (metafieldsScript) {
      try {
        const data = JSON.parse(metafieldsScript.textContent);
        const productMetafields = data.productMetafields || {};

        const isKeychain = productMetafields.iskeychain;
        if (
          isKeychain === "yes" ||
          isKeychain === "YES" ||
          isKeychain === "Yes" ||
          isKeychain === true
        ) {
          return true;
        }
      } catch (e) {}
    }

    // Fallback: Get from global variable
    const selectedOptions = window.selectedProductOptions || {};
    if (selectedOptions.iskeychain === "yes" || selectedOptions.iskeychain === true) {
      return true;
    }

    return false;
  }

  /**
   * Detect if current product is a Phone case product
   * @returns {boolean} Whether this is a Phone case product
   */
  detectIsPhone() {
    // Get current active container
    const container = this.getCurrentContainer();

    // Read isPhone from product metafields JSON
    const metafieldsScript = container.querySelector(
      ".variant-metafields-data",
    );
    if (metafieldsScript) {
      try {
        const data = JSON.parse(metafieldsScript.textContent);
        const productMetafields = data.productMetafields || {};

        const isPhone = productMetafields.isPhone;
        if (
          isPhone === "yes" ||
          isPhone === "YES" ||
          isPhone === "Yes" ||
          isPhone === true
        ) {
          return true;
        }
      } catch (e) {}
    }

    // Fallback: Get from global variable
    const selectedOptions = window.selectedProductOptions || {};
    if (selectedOptions.isPhone === "yes" || selectedOptions.isPhone === true) {
      return true;
    }

    return false;
  }

  /**
   * Detect if current product is a face swapping doll product
   * @returns {boolean} Whether this is a face swapping doll product
   */
  detectIsFaceSwappingDoll() {
    const container = this.getCurrentContainer();

    const metafieldsScript = container.querySelector(
      ".variant-metafields-data",
    );
    if (metafieldsScript) {
      try {
        const data = JSON.parse(metafieldsScript.textContent);
        const productMetafields = data.productMetafields || {};

        const isFaceSwappingDoll = productMetafields.isFaceSwappingDoll;
        if (
          isFaceSwappingDoll === "yes" ||
          isFaceSwappingDoll === "YES" ||
          isFaceSwappingDoll === "Yes" ||
          isFaceSwappingDoll === true
        ) {
          return true;
        }
      } catch (e) {}
    }

    const selectedOptions = window.selectedProductOptions || {};
    return (
      selectedOptions.isFaceSwappingDoll === "yes" ||
      selectedOptions.isFaceSwappingDoll === true
    );
  }

  /**
   * Detect if current product is an Aroma Stone product
   * @returns {boolean} Whether this is an Aroma Stone product
   */
  detectIsAromaStone() {
    const container = this.getCurrentContainer();

    const metafieldsScript = container.querySelector(
      ".variant-metafields-data",
    );
    if (metafieldsScript) {
      try {
        const data = JSON.parse(metafieldsScript.textContent);
        const productMetafields = data.productMetafields || {};

        const aromaStone = productMetafields.aromaStone;
        if (
          aromaStone === "yes" ||
          aromaStone === "YES" ||
          aromaStone === "Yes" ||
          aromaStone === true
        ) {
          return true;
        }
      } catch (e) {}
    }

    const selectedOptions = window.selectedProductOptions || {};
    return (
      selectedOptions.aromaStone === "yes" ||
      selectedOptions.aromaStone === true
    );
  }

  /**
   * 上传图片到后端
   */
  async uploadImage(file) {
    // 获取当前选中的 Variant ID
    let currentVariantId = window.currentSelectedVariant?.id || null;
    const currentContainer = this.getCurrentContainer();
    const scopedVariantInput =
      currentContainer?.querySelector('input[name="id"]') ||
      currentContainer?.querySelector('select[name="id"]');

    if (!currentVariantId && scopedVariantInput?.value) {
      currentVariantId = scopedVariantInput.value;
    }

    if (!currentVariantId) {
      // 兜底：遍历所有 select[name="id"] 和 input[name="id"]，取最后一个有值的
      const allSelects = document.querySelectorAll('select[name="id"]');
      const allInputs = document.querySelectorAll('input[name="id"]');

      // 优先取 select（variant selector）
      if (allSelects.length > 0) {
        currentVariantId = allSelects[allSelects.length - 1].value;
      }
      // 再试 hidden input
      if (!currentVariantId && allInputs.length > 0) {
        currentVariantId = allInputs[allInputs.length - 1].value;
      }
    }

    // 从全局变量获取用户选择的 size
    const selectedOptions = window.selectedProductOptions || {};
    const selectedSize = selectedOptions.size || "";

    // 使用 getCurrentStyle() 获取当前选中的风格（已经映射为 API 格式）
    const apiStyleType = this.getCurrentStyle();
    const requestedJobNum = apiStyleType === "3D-Embossed-Lightbox" ? 1 : this.config.jobNum;
    this.config.jobNum = requestedJobNum;
    const badgeSize = this.getCurrentSize();
    // 检测是否为 Badge 产品
    const isBadge = this.detectIsBadge();

    // 检测角色类型
    const characterType =
      this.preparedCharacterType || (await this.detectCharacterType());

    // 检测是否为 Keychain 产品
    const isKeychain = this.detectIsKeychain();

    // 检测是否为 Phone case 产品
    const isPhone = this.detectIsPhone();

    // 检测是否为换脸娃娃产品
    const isFaceSwappingDoll = this.detectIsFaceSwappingDoll();

    // 检测是否为 Aroma Stone 产品
    const isAromaStone = this.detectIsAromaStone();

    const normalizedCharacterType =
      isBadge && (characterType === "pet" || characterType === "human-pet")
        ? "group"
        : characterType;

    const parameterContext = {
      type: this.config.sourceType,
      QImageNum: "1",
      jobNum: this.config.jobNum,
      styleType: apiStyleType,
      sourcePage: "deliver3D",
      fromSource: "deliver3D",
      characterType: normalizedCharacterType,
    };

    if (isBadge) {
      parameterContext.category = "portrait";
      if (currentVariantId) {
        parameterContext.variantId = currentVariantId;
      }
      if(badgeSize){
        parameterContext.SKUSize = badgeSize;
      }
    }

    if (isKeychain) {
      parameterContext.keyChain = "true";
    }

    if (isPhone) {
      parameterContext.category = "phone_case";
      let phoneCaseStyle = {
        'Q-Style': 'chibi',
        'Pixar-Style':'pixar',
        'Funko-Style':'funko'
      }
      parameterContext.styleType = phoneCaseStyle[apiStyleType] || apiStyleType;
    }

    if (isFaceSwappingDoll) {
      parameterContext.type = "cute_single_img2img_deliver3d_nano";
      parameterContext.styleType = "Face-Swap-Doll";
    }

    if (apiStyleType === "3D-Embossed-Lightbox") {
      parameterContext.type = "cute_single_img2img_deliver3d_image_2";
      parameterContext.jobNum = 1;
    }

    if (isAromaStone) {
      parameterContext.styleType = "Aroma-Stone";
      parameterContext.type = "cute_single_img2img_deliver3d_long";
    }

    if (
      !isAromaStone &&
      characterType === "pet" &&
      apiStyleType === "Q-Style"
    ) {
      parameterContext.type = "cute_single_img2img_deliver3d_nano";
    }

    const formData = new FormData();
    formData.append("images", file);
    formData.append("parameterContext", JSON.stringify(parameterContext));
    const authToken = await this.ensureAuthToken();
    if (!authToken) {
      throw new Error("No authentication token. Please refresh the page.");
    }

    const signal = window.globalUploadModal.abortController?.signal;
    let response;
    try {
      response = await fetch(
        this.config.apiBaseUrl + this.config.uploadEndpoint,
        {
          method: "POST",
          headers: {
            Authorization: authToken,
          },
          body: formData,
          signal: signal,
        },
      );
    } catch (error) {
      throw this.markErrorSourceStage(error, "upload");
    }

    const data = await response.json();

    if (data.statusType === -1) {
      const rateLimitError = new Error(
        "It looks like this task has been submitted multiple times. Please contact support if you're experiencing issues.",
      );
      rateLimitError.isRateLimit = true;
      rateLimitError.originalMessage =
        data.message || data.error || "Status type -1";
      throw rateLimitError;
    }

    // 检查是否为限频错误
    if (data.success === false && data.failures && data.failures.length > 0) {
      const failure = data.failures[0];

      // 检查是否包含限频相关的错误信息
      if (failure.error && failure.error.includes("exceeds the limit")) {
        const rateLimitError = new Error(
          "Looks like you're a power user! Contact us to see how we can better support your needs.",
        );
        rateLimitError.isRateLimit = true;
        rateLimitError.originalMessage = failure.error;
        throw rateLimitError;
      }

      // Ignore "Invalid parameters found: category" error - continue with upload
      if (
        failure.error &&
        failure.error.includes("Invalid parameters found: category")
      ) {
        // Don't throw, let the upload continue
        throw new Error("Upload failed");
      } else {
        // 其他失败错误
        throw new Error(failure.error || "Upload failed");
      }
    }

    if (!data.success || !data.results?.[0]?.uuids) {
      throw new Error("Invalid response from server");
    }

    if (!response.ok) {
      throw new Error("Upload failed: " + response.statusText);
    }

    return data.results[0].uuids;
  }

  /**
   * 构建查询历史接口的 URL
   */
  async buildPollUrl() {
    let pollUrl = this.config.apiBaseUrl + this.config.pollEndpoint;
    return pollUrl;
  }

  /**
   * 调用一次查询历史接口
   */
  async fetchTaskOnce(pollUrl, uuids) {
    const authToken = await this.ensureAuthToken();
    if (!authToken) throw new Error("No authentication token");
    const signal = window.globalUploadModal.abortController?.signal;

    let response;
    try {
      response = await fetch(pollUrl, {
        method: "POST",
        headers: {
          Authorization: authToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ uuids }),
        signal: signal,
      });
    } catch (error) {
      throw this.markErrorSourceStage(error, "poll");
    }

    if (!response.ok) {
      const error = new Error("Server error: " + response.statusText);
      throw this.markErrorSourceStage(error, "poll");
    }

    return response.json();
  }

  /**
   * 通过 WebSocket 监听 common_generated_notification 事件，
   * 每次收到通知后调用一次查询历史接口获取最新结果。
   * 兜底：socket 重连后主动查一次；定时兜底查询防止通知丢失。
   */
  async pollImages(uuids) {
    const completedImages = new Set();
    let hasInitializedGrid = false;
    const isEmbossedLightbox = this.getCurrentStyle() === "3D-Embossed-Lightbox";

    const STATUS = {
      COMPLETED: "completed",
      COMPLETE: "complete",
      FAILED: "failed",
    };

    // 获取全局 socket 实例（允许正在连接中）。WS 不可用时继续使用 HTTP 轮询兜底。
    let socket = null;
    try {
      socket = await this.ensureSocketReady();
    } catch (error) {}

    const pollUrl = await this.buildPollUrl();
    const timeoutMs = this.config.pollInterval * this.config.maxPollAttempts;
    const FALLBACK_INTERVAL = 90000; // 每 90 秒兜底查一次

    return new Promise((resolve, reject) => {
      let settled = false;

      const cleanup = () => {
        if (socket) {
          socket.off("common_generated_notification", onNotification);
          socket.io.off("reconnect", onReconnect);
        }
        clearTimeout(timer);
        clearInterval(fallbackTimer);
      };

      const settle = (fn) => {
        if (settled) return;
        settled = true;
        cleanup();
        fn();
      };

      // 超时
      const timer = setTimeout(() => {
        settle(() => reject(new Error("Processing timeout - please try again")));
      }, timeoutMs);

      // 取消监听
      const abortSignal = window.globalUploadModal.abortController?.signal;
      if (abortSignal) {
        const onAbort = () => {
          settle(() => reject(new DOMException("Upload cancelled", "AbortError")));
        };
        if (abortSignal.aborted) { onAbort(); return; }
        abortSignal.addEventListener("abort", onAbort, { once: true });
      }

      // 处理历史接口返回的 batches
      const processBatches = (batches) => {
        if (!batches || !batches.length) return;

        const orderedBatches = batches.slice().sort((a, b) => {
          const aId = Number(a.jobId || a.jobid || 0);
          const bId = Number(b.jobId || b.jobid || 0);
          return aId - bId;
        }).slice(0, Math.max(1, Number(this.config.jobNum) || 2));

        orderedBatches.forEach((batch, index) => {
          const status = batch.status?.toLowerCase();
          const batchId = batch.jobId || batch.jobid || index;

          if (completedImages.has(batchId)) return;

          const lightboxImageUrl = batch.batchData?.fileKeyImg1 || "";
          if (isEmbossedLightbox && lightboxImageUrl) {
            if (!hasInitializedGrid) {
              window.globalUploadModal.initImageSelection(this.config.jobNum);
              hasInitializedGrid = true;
            }
            window.globalUploadModal.updateImageSlot(
              index,
              lightboxImageUrl,
              lightboxImageUrl,
              batchId,
            );
            completedImages.add(batchId);
            if (typeof GtagUtils !== "undefined") {
              GtagUtils.trackGenerated("g_1", {
                job_id: String(batchId),
                generation_model: GtagUtils.getModelName(this.config.sourceType),
              });
            }
            settle(() => resolve([{
              jobId: batchId,
              url: lightboxImageUrl,
              displayUrl: lightboxImageUrl,
              status: batch.status,
            }]));
            return;
          }

          if (status === STATUS.COMPLETED || status === STATUS.COMPLETE) {
            const url = batch.batchData?.fileKeyImg0 || "";
            const displayUrl = batch.batchData?.fileKeyImg1 || batch.batchData?.fileKeyImg0 || "";
            if (url) {
              if (!hasInitializedGrid) {
                window.globalUploadModal.initImageSelection(this.config.jobNum);
                hasInitializedGrid = true;
              }
              window.globalUploadModal.updateImageSlot(index, url, displayUrl, batchId);
              completedImages.add(batchId);
              if (typeof GtagUtils !== "undefined") {
                const stage = completedImages.size === 1 ? "g_1" : "g_2";
                GtagUtils.trackGenerated(stage, {
                  job_id: String(batchId),
                  generation_model: GtagUtils.getModelName(this.config.sourceType),
                });
              }
            }
          } else if (status === STATUS.FAILED) {
            if (!hasInitializedGrid) {
              window.globalUploadModal.initImageSelection(this.config.jobNum);
              hasInitializedGrid = true;
            }
            window.globalUploadModal.updateImageSlotFailed(index);
            completedImages.add(batchId);
          }
        });

        // 检查是否全部终态
        const allTerminal = orderedBatches.every((b) => {
          const s = b.status?.toLowerCase();
          return s === STATUS.COMPLETED || s === STATUS.COMPLETE || s === STATUS.FAILED;
        });

        if (!allTerminal) return;

        const allFailed = orderedBatches.every((b) => b.status?.toLowerCase() === STATUS.FAILED);
        if (allFailed) {
          window.globalUploadModal.showError("", "Please try again with a different photo.");
          const error = new Error("All images generation failed");
          error.isAllFailed = true;
          settle(() => reject(error));
          return;
        }

        if (completedImages.size >= orderedBatches.length) {
          const results = orderedBatches
            .map((b) => ({
              jobId: b.jobId || b.jobid,
              url: b.batchData?.fileKeyImg0 || "",
              displayUrl: b.batchData?.fileKeyImg1 || b.batchData?.fileKeyImg0 || "",
              status: b.status,
            }))
            .filter((img) => img.url);
          settle(() => resolve(results));
        }
      };

      // 查一次历史接口并处理结果
      const fetchAndProcess = async (reason) => {
        if (settled) return;
        try {
          const result = await this.fetchTaskOnce(pollUrl, uuids);
          if (!result.success || !result.data?.length) return;
          const batches = result.data || [];
          processBatches(batches);
        } catch (error) {
          if (error.name === "AbortError") {
            settle(() => reject(error));
          } else if (error.isAllFailed) {
            settle(() => reject(error));
          }
        }
      };

      // 1. 收到 WS 通知后查一次
      const onNotification = (data) => {
        fetchAndProcess("ws_notification");
      };

      // 2. socket 重连后主动查一次（补偿断线期间丢失的通知）
      // socket.io v4: reconnect 事件在 socket.io (Manager) 上触发，不在 socket 实例上
      const onReconnect = (attemptNumber) => {
        fetchAndProcess("reconnect");
      };

      // 3. 定时兜底查询（防止 WS 通知丢失）
      const fallbackTimer = setInterval(() => {
        fetchAndProcess("fallback_interval");
      }, socket ? FALLBACK_INTERVAL : this.config.pollInterval);

      fetchAndProcess(socket ? "initial_check" : "http_polling_fallback");

      if (socket) {
        socket.on("common_generated_notification", onNotification);
        socket.io.on("reconnect", onReconnect);
      }
    });
  }

  /**
   * 获取当前选中的风格
   */
  getCurrentStyle() {
    // 获取当前活跃的容器
    const container = this.getCurrentContainer();
    // 方法1: 从全局变量获取（优先）
    const selectedOptions = window.selectedProductOptions;
    if (selectedOptions && selectedOptions.style) {
      const mappedStyle = this.styleMapping[selectedOptions.style];
      if (mappedStyle) {
        return mappedStyle;
      }
    }

    // 方法2: 从当前容器中查找所有选中的 radio
    const allCheckedRadios = container.querySelectorAll(
      'input[type="radio"][name^="option"]:checked',
    );

    // 智能查找风格选项：检查值是否在映射表中
    for (const radio of allCheckedRadios) {
      const value = radio.value;
      const mappedStyle = this.styleMapping[value];
      if (mappedStyle) {
        return mappedStyle;
      }
    }

    // 方法3: 从当前容器中的显示值获取（降级）
    const allValueDisplays = container.querySelectorAll(
      '[id*="option"][id*="-value"]',
    );
    for (const display of allValueDisplays) {
      const value = display.textContent.trim();
      const mappedStyle = this.styleMapping[value];
      if (mappedStyle) {
        return mappedStyle;
      }
    }

    // 方法4: 从当前容器中的选项选择器获取（最终降级）
    const allOptionSelectors = container.querySelectorAll(
      ".product-form__option-selector",
    );
    for (let index = 0; index < allOptionSelectors.length; index++) {
      const selector = allOptionSelectors[index];
      const checkedInput = selector.querySelector("input:checked");

      if (checkedInput) {
        const mappedStyle = this.styleMapping[checkedInput.value];
        if (mappedStyle) {
          return mappedStyle;
        }
      }
    }

    return this.config.defaultStyle;
  }

  /**
   * 获取相框的size
   */
  getCurrentSize() {
    // 获取当前活跃的容器
    const container = this.getCurrentContainer();
    // 方法1: 从全局变量获取（优先）
    const selectedOptions = window.selectedProductOptions;
    if (selectedOptions && selectedOptions.style) {
      const mappedSize = this.sizes[selectedOptions.style];
      if (mappedSize) {
        return mappedSize;
      }
    }

    // 方法2: 从当前容器中查找所有选中的 radio
    const allCheckedRadios = container.querySelectorAll(
      'input[type="radio"][name^="option"]:checked',
    );

    // 智能查找风格选项：检查值是否在映射表中
    for (const radio of allCheckedRadios) {
      const value = radio.value;
      const mappedSize = this.sizes[value];
      if (mappedSize) {
        return mappedSize;
      }
    }

    // 方法3: 从当前容器中的显示值获取（降级）
    const allValueDisplays = container.querySelectorAll(
      '[id*="option"][id*="-value"]',
    );
    for (const display of allValueDisplays) {
      const value = display.textContent.trim();
      const mappedSize = this.sizes[value];
      if (mappedSize) {
        return mappedSize;
      }
    }

    // 方法4: 从当前容器中的选项选择器获取（最终降级）
    const allOptionSelectors = container.querySelectorAll(
      ".product-form__option-selector",
    );
    for (let index = 0; index < allOptionSelectors.length; index++) {
      const selector = allOptionSelectors[index];
      const checkedInput = selector.querySelector("input:checked");

      if (checkedInput) {
        const mappedSize = this.sizes[checkedInput.value];
        if (mappedSize) {
          return mappedSize;
        }
      }
    }

    return this.sizes[0];
  }

  /**
   * 保存图片 URL
   */
  saveImageUrl(url, displayUrl, jobId) {
    if (this.hiddenInput) {
      this.hiddenInput.value = url;
    }

    // Save display URL to the corresponding display hidden input
    if (displayUrl) {
      const displayInputId = this.hiddenInput?.id
        ? this.hiddenInput.id + "-display"
        : null;
      if (displayInputId) {
        // Find the display input that belongs to the same form as hiddenInput
        const formId = this.hiddenInput.getAttribute('form');
        const allDisplayInputs = document.querySelectorAll(`[id="${displayInputId}"]`);
        const displayInput = Array.from(allDisplayInputs).find(
          el => el.getAttribute('form') === formId
        ) || document.getElementById(displayInputId);
        if (displayInput) {
          displayInput.value = displayUrl;
        }
      }
    }

    // Save jobId to the corresponding jobId hidden input
    if (jobId) {
      const jobIdInputId = this.hiddenInput?.id
        ? this.hiddenInput.id + "-job-id"
        : null;
      if (jobIdInputId) {
        // Find the jobId input that belongs to the same form as hiddenInput
        const formId = this.hiddenInput.getAttribute('form');
        const allJobIdInputs = document.querySelectorAll(`[id="${jobIdInputId}"]`);
        const jobIdInput = Array.from(allJobIdInputs).find(
          el => el.getAttribute('form') === formId
        ) || document.getElementById(jobIdInputId);
        if (jobIdInput) {
          jobIdInput.value = jobId;
        }
      }
    }
  }

  /**
   * 判断是否为网络错误
   */
  isNetworkError(error) {
    if (!error) return false;

    const errorMessage = error.message?.toLowerCase() || "";
    const errorName = error.name?.toLowerCase() || "";

    // 常见的网络错误特征
    const networkErrorPatterns = [
      "failed to fetch", // fetch 失败
      "load failed", // Safari fetch 失败
      "network request failed", // 网络请求失败
      "networkerror", // 网络错误
      "network error", // 网络错误
      "request timeout", // 请求超时
      "timeout", // 超时
      "connection", // 连接问题
      "unreachable", // 无法访问
      "offline", // 离线
      "dns", // DNS 问题
      "enotfound", // DNS 未找到
      "econnrefused", // 连接被拒绝
      "econnreset", // 连接重置
      "etimedout", // 超时
    ];

    // 检查错误消息是否包含网络错误关键词
    const hasNetworkPattern = networkErrorPatterns.some((pattern) =>
      errorMessage.includes(pattern),
    );

    // 检查是否为 TypeError (通常是 fetch 失败)
    const isTypeError =
      errorName === "typeerror" && errorMessage.includes("fetch");

    return hasNetworkPattern || isTypeError;
  }

  getUserFacingError(error) {
    if (!error) {
      return new Error("The system is busy. Please try again.");
    }

    if (error.isAllFailed || error.isRateLimit) {
      return error;
    }

    if (this.isNetworkError(error)) {
      const networkError = new Error(
        "Network connection issue. Please refresh the page and try again.",
      );
      networkError.isNetworkError = true;
      networkError.originalError = error;
      return networkError;
    }

    const userFacingError = new Error("The system is busy. Please try again.");
    userFacingError.isInternalError = true;
    userFacingError.originalError = error;
    return userFacingError;
  }

  /**
   * 获取认证 Token
   * 如果 token 不存在，等待 GuestAccount 初始化完成
   */
  async ensureAuthToken() {
    let token = this.getStoredAuthToken();
    if (token) return "Bearer " + token;

    const guestAccount = await this.waitForGuestAccount();

    // Token 不存在，等待 GuestAccount 初始化
    if (guestAccount && typeof guestAccount.init === "function") {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const result = await guestAccount.init();
          token = result?.token || this.getStoredAuthToken();
          if (token) {
            return "Bearer " + token;
          }
        } catch (e) {}

        await this.sleep(500 * (attempt + 1));
      }

      const initError = guestAccount.lastInitError;
      if (initError && this.isNetworkError(initError)) {
        throw initError;
      }
    }

    // 兜底：轮询等待 token，最多等 30 秒
    for (let i = 0; i < 60; i++) {
      await this.sleep(500);
      token = this.getStoredAuthToken();
      if (token) {
        return "Bearer " + token;
      }
    }

    return null;
  }

  getAuthToken() {
    const token = this.getStoredAuthToken();
    return token ? "Bearer " + token : null;
  }

  getStoredAuthToken() {
    const token = (() => {
      try {
        return localStorage.getItem(this.config.tokenStorageKey) || window.guestToken;
      } catch (error) {
        return window.guestToken;
      }
    })();

    if (
      typeof token !== "string" ||
      token.trim() === "" ||
      token === "undefined" ||
      token === "null"
    ) {
      return null;
    }

    return token;
  }

  async waitForGuestAccount(timeoutMs = 3000) {
    const startTime = Date.now();
    while (Date.now() - startTime < timeoutMs) {
      if (window.GuestAccount && typeof window.GuestAccount.init === "function") {
        return window.GuestAccount;
      }
      await this.sleep(100);
    }
    return null;
  }

  /**
   * 延迟函数
   */
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * 取消上传
   */
  cancel() {
    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.progressAnimation) {
      this.progressAnimation.stop();
    }
  }

  /**
   * 销毁实例
   */
  destroy() {
    this.cancel();
    this.disconnectSocket();

    // 清理事件监听器
    if (this.trigger && this.triggerHandler) {
      this.trigger.removeEventListener("click", this.triggerHandler);
    }
    if (this.fileInput && this.fileInputHandler) {
      this.fileInput.removeEventListener("change", this.fileInputHandler);
    }

    // 清空引用
    this.trigger = null;
    this.fileInput = null;
    this.hiddenInput = null;
    this.abortController = null;
    this.progressAnimation = null;
  }
}

// 导出到全局
window.ImageUploader = ImageUploader;

// 页面加载完成后自动创建全局实例并连接 WebSocket
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initGlobalSocket);
} else {
  initGlobalSocket();
}

function initGlobalSocket() {
  if (!window.globalImageUploader) {
    window.globalImageUploader = new ImageUploader();
  }
  // 自动连接 socket
  window.globalImageUploader.connectSocket();
}
